/**
 * Design Tokens - AIO.dog
 *
 * Sistema de tokens para consistência visual seguindo Material Design 3
 * Baseado em 8-point grid system
 */

// =============================================================================
// SPACING (8-point grid)
// =============================================================================
export const spacing = {
  /** 4px - micro spacing */
  xs: '0.25rem',
  /** 8px - tight spacing */
  sm: '0.5rem',
  /** 12px - element spacing */
  md: '0.75rem',
  /** 16px - standard spacing */
  lg: '1rem',
  /** 24px - section spacing */
  xl: '1.5rem',
  /** 32px - large section spacing */
  '2xl': '2rem',
  /** 48px - page section spacing */
  '3xl': '3rem',
} as const;

// =============================================================================
// TYPOGRAPHY SCALE
// =============================================================================
export const fontSize = {
  /** 12px - captions, badges */
  xs: '0.75rem',
  /** 14px - labels, secondary text */
  sm: '0.875rem',
  /** 16px - body text */
  base: '1rem',
  /** 18px - subheadings */
  lg: '1.125rem',
  /** 20px - headings */
  xl: '1.25rem',
  /** 24px - section titles */
  '2xl': '1.5rem',
  /** 30px - page titles */
  '3xl': '1.875rem',
} as const;

export const fontWeight = {
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

export const lineHeight = {
  tight: '1.25',
  normal: '1.5',
  relaxed: '1.75',
} as const;

// =============================================================================
// ICON SIZES
// =============================================================================
export const iconSize = {
  /** 16px - inline icons */
  xs: 16,
  /** 20px - button icons */
  sm: 20,
  /** 24px - standard icons */
  md: 24,
  /** 32px - large icons */
  lg: 32,
} as const;

// =============================================================================
// SHADOWS (Material Design 3 inspired)
// =============================================================================
export const shadow = {
  /** Subtle elevation for cards at rest */
  soft: '0 1px 3px rgba(0, 0, 0, 0.08)',
  /** Standard card elevation */
  card: '0 2px 8px rgba(0, 0, 0, 0.08)',
  /** Elevated elements (modals, dropdowns) */
  elevated: '0 4px 16px rgba(0, 0, 0, 0.12)',
  /** High elevation (dialogs, popovers) */
  high: '0 8px 24px rgba(0, 0, 0, 0.16)',
} as const;

// =============================================================================
// BORDER RADIUS
// =============================================================================
export const borderRadius = {
  /** 4px - subtle rounding */
  sm: '0.25rem',
  /** 8px - buttons, inputs */
  md: '0.5rem',
  /** 12px - cards */
  lg: '0.75rem',
  /** 16px - modals, large cards */
  xl: '1rem',
  /** 24px - pills, tags */
  '2xl': '1.5rem',
  /** Full circle */
  full: '9999px',
} as const;

// =============================================================================
// TRANSITIONS
// =============================================================================
export const transition = {
  /** Fast micro-interactions */
  fast: '150ms ease-out',
  /** Standard transitions */
  normal: '200ms ease-out',
  /** Slower, more deliberate transitions */
  slow: '300ms ease-out',
} as const;

// =============================================================================
// TOUCH TARGETS (WCAG 2.1 AA)
// =============================================================================
export const touchTarget = {
  /** Minimum touch target size */
  min: '44px',
  /** Comfortable touch target */
  comfortable: '48px',
} as const;

// =============================================================================
// Z-INDEX SCALE
// =============================================================================
export const zIndex = {
  base: 0,
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  modalBackdrop: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
} as const;

// =============================================================================
// SEMANTIC COLORS (CSS variable references)
// =============================================================================
export const colors = {
  primary: {
    50: 'var(--color-primary-50)',
    100: 'var(--color-primary-100)',
    200: 'var(--color-primary-200)',
    300: 'var(--color-primary-300)',
    400: 'var(--color-primary-400)',
    500: 'var(--color-primary-500)',
    600: 'var(--color-primary-600)',
    700: 'var(--color-primary-700)',
    800: 'var(--color-primary-800)',
    900: 'var(--color-primary-900)',
  },
  success: {
    light: 'var(--color-success-100)',
    main: 'var(--color-success-500)',
    dark: 'var(--color-success-700)',
  },
  danger: {
    light: 'var(--color-danger-100)',
    main: 'var(--color-danger-500)',
    dark: 'var(--color-danger-700)',
  },
  warning: {
    light: 'var(--color-warning-100)',
    main: 'var(--color-warning-500)',
    dark: 'var(--color-warning-700)',
  },
  info: {
    light: 'var(--color-info-100)',
    main: 'var(--color-info-500)',
    dark: 'var(--color-info-700)',
  },
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================
export type Spacing = keyof typeof spacing;
export type FontSize = keyof typeof fontSize;
export type FontWeight = keyof typeof fontWeight;
export type IconSize = keyof typeof iconSize;
export type Shadow = keyof typeof shadow;
export type BorderRadius = keyof typeof borderRadius;
export type Transition = keyof typeof transition;
export type ZIndex = keyof typeof zIndex;
